import EmptyState from "@/components/empty-state";
import InputSearch from "@/components/input-search";
import RadioFilter, { RadioFilterItem } from "@/components/radio-filter";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { usePreviousEditions } from "@/hooks/previous-editions.hook";
import { QuestionnaireResultStatus } from "@/types/questionnaries";
import { Book1 } from "iconsax-react";
import { useDebounce } from "use-debounce";
import {
  usePreviousEditionsActions,
  usePreviousEditionsEditionNameFilter,
  usePreviousEditionsPagination,
  usePreviousEditionsResultsFilter,
} from "../previous-editions.store";
import { columns } from "./columns";

const PreviousEditionsTable = () => {
  const editionNameFilter = usePreviousEditionsEditionNameFilter();
  const [debouncedEditionNameFilter] = useDebounce(editionNameFilter, 500);

  const availableResultsFilter: RadioFilterItem[] = [
    {
      id: "APPROVED",
      value: "Aprobado",
      enabled: true,
    },
    {
      id: "FAILED",
      value: "Desaprobado",
      enabled: true,
    },
    {
      id: "NOT_EVALUATED",
      value: "No evaluado",
      enabled: true,
    },
  ];

  const resultsFilter = usePreviousEditionsResultsFilter();

  const pagination = usePreviousEditionsPagination();

  const actions = usePreviousEditionsActions();

  const { data: userProfile } = useCurrentUserProfile();

  const { data: previousEditions, isLoading: isPreviousEditionsLoading } =
    usePreviousEditions({
      companyId: userProfile?.companyId || "",
      editionName: debouncedEditionNameFilter,
      resultStatus: resultsFilter as QuestionnaireResultStatus | undefined,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    });

  const hasFilterApplied =
    debouncedEditionNameFilter.trim().length > 0 || resultsFilter;

  const handleResetFilters = () => {
    actions.resetFilters();
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <InputSearch
          value={editionNameFilter}
          placeholder="Buscar por edición"
          onChange={actions.setEditionNameFilter}
          className="max-w-sm grow"
        />

        <RadioFilter
          label="Resultado"
          items={availableResultsFilter}
          selectedId={resultsFilter === "" ? undefined : resultsFilter}
          onValueChange={(item) => {
            actions.setResultsFilter(item?.id || "");
          }}
        />
      </div>

      <DataTable
        className="mt-4 flex grow flex-col"
        columns={columns}
        data={previousEditions?.content || []}
        manualPagination={true}
        rowCount={previousEditions?.totalElements || 0}
        onPaginationChange={(updaterOrValue) =>
          actions.setPagination(
            typeof updaterOrValue === "function"
              ? updaterOrValue(pagination)
              : updaterOrValue,
          )
        }
        pagination={pagination}
        emptyState={
          <EmptyState
            icon={Book1}
            className="mx-auto my-8"
            message={
              hasFilterApplied
                ? "No encontramos ninguna edición pasada que coincida con tus criterios de búsqueda. Prueba ajustando los filtros para ver más resultados."
                : "No hay ediciones pasadas aún."
            }
          >
            {hasFilterApplied && (
              <Button
                className="mt-4"
                variant={"outline"}
                onClick={handleResetFilters}
              >
                Quitar filtros
              </Button>
            )}
          </EmptyState>
        }
        isLoading={isPreviousEditionsLoading}
      />
    </>
  );
};

export default PreviousEditionsTable;
